import { useState } from "react";
import { api } from "../../api/apiClient";
import type { UserListEntry } from "../../api/types";
import { Modal } from "../Modal";
import { accountBadge, inviteSentence } from "./teamLabels";

/**
 * G-136 — the invitation of someone who has not set up their account yet.
 * Owner-only, like creating the person.
 *
 * "Send a new invitation" kills the old link in the same write, so there is
 * never more than one that works. "Cancel" leaves the person on the team —
 * schedule, payroll and shop stay as they are — with no way to sign in until
 * a new invitation is sent; removing them is Delete, as for anyone.
 *
 * A wrong address is fixed under Contact: for someone awaiting setup the
 * address changes at once and a fresh invitation goes to it.
 */
export function InviteModal({
  user,
  onClose,
  onChanged,
}: {
  user: UserListEntry;
  onClose: () => void;
  onChanged: (updated: UserListEntry) => void;
}) {
  const [busy, setBusy] = useState<"resend" | "cancel" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const badge = accountBadge(user);
  const hasLiveInvite =
    user.accountStatus === "INVITE_SENDING" ||
    user.accountStatus === "INVITED" ||
    user.accountStatus === "INVITE_FAILED";

  const act = async (kind: "resend" | "cancel") => {
    setError(null);
    setNotice(null);
    setBusy(kind);
    try {
      const updated =
        kind === "resend" ? await api.resendInvite(user.id) : await api.cancelInvite(user.id);
      onChanged(updated);
      setNotice(
        kind === "resend"
          ? `A new invitation is on its way to ${updated.email}. The earlier link no longer works.`
          : "The invitation was cancelled. Its link no longer works.",
      );
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not update the invitation");
    } finally {
      setBusy(null);
    }
  };

  return (
    <Modal title={`Invitation · ${user.fullName}`} onClose={onClose}>
      <p>
        {badge ? <span className={badge.className}>{badge.label}</span> : null}{" "}
        {inviteSentence(user)}
      </p>
      <p className="muted">
        Sent to <strong>{user.email}</strong>. A wrong address is fixed under Contact, which
        sends a new invitation to the corrected one.
      </p>

      {notice ? <div className="alert success">{notice}</div> : null}
      {error ? <div className="alert error">{error}</div> : null}

      <div className="button-row">
        {hasLiveInvite ? (
          <button
            type="button"
            className="button danger"
            onClick={() => void act("cancel")}
            disabled={busy !== null}
          >
            {busy === "cancel" ? "Cancelling…" : "Cancel invitation"}
          </button>
        ) : null}
        <button
          type="button"
          className="button"
          onClick={() => void act("resend")}
          disabled={busy !== null}
        >
          {busy === "resend" ? "Sending…" : "Send a new invitation"}
        </button>
      </div>
    </Modal>
  );
}
